// ============================================
// Therapist Photo Management
// ============================================

import React, { useRef, useCallback } from 'react';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { db, storage } from '../../../config/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Event, EventTherapist } from '../../../types';

interface Props {
  event: Event;
}

// Helper to create a centered aspect crop
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export default function TherapistPhotos({ event }: Props) {
  const { user } = useAuth();
  const [therapists, setTherapists] = React.useState<EventTherapist[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedTherapist, setSelectedTherapist] = React.useState<EventTherapist | null>(null);

  // Two-step cropping
  const [step, setStep] = React.useState<'portrait' | 'thumbnail'>('portrait');

  // Portrait crop (1:3)
  const [portraitCrop, setPortraitCrop] = React.useState<Crop>();
  const [completedPortraitCrop, setCompletedPortraitCrop] = React.useState<PixelCrop>();

  // Thumbnail crop (1:1 square)
  const [thumbCrop, setThumbCrop] = React.useState<Crop>();
  const [completedThumbCrop, setCompletedThumbCrop] = React.useState<PixelCrop>();

  const [saving, setSaving] = React.useState(false);
  const [uploadFile, setUploadFile] = React.useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = React.useState<string | null>(null);

  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Aspect ratios
  const PORTRAIT_ASPECT = 1 / 3;
  const THUMB_ASPECT = 1;

  React.useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'events', event.id, 'therapists'),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        })) as EventTherapist[];
        // Sort: pending first, then by name
        data.sort((a, b) => {
          if (a.photoStatus === 'pending' && b.photoStatus !== 'pending') return -1;
          if (b.photoStatus === 'pending' && a.photoStatus !== 'pending') return 1;
          return a.displayName.localeCompare(b.displayName);
        });
        setTherapists(data);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [event.id]);

  const onImageLoadPortrait = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setPortraitCrop(centerAspectCrop(width, height, PORTRAIT_ASPECT));
  }, []);

  const onImageLoadThumb = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    // Default thumbnail crop - centered square that's 30% of image width
    const cropSize = Math.min(width, height) * 0.5;
    setThumbCrop({
      unit: 'px',
      x: (width - cropSize) / 2,
      y: height * 0.1, // Start near top for face
      width: cropSize,
      height: cropSize,
    });
  }, []);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setUploadPreview(URL.createObjectURL(file));
    setStep('portrait');
  };

  // Generate 1:3 portrait crop
  const getCroppedImg = async (
    image: HTMLImageElement,
    crop: PixelCrop
  ): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Output dimensions - width 400px, height 1200px (1:3 ratio)
    const outputWidth = 400;
    const outputHeight = 1200;

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      outputWidth,
      outputHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas to Blob failed'));
        },
        'image/jpeg',
        0.9
      );
    });
  };

  // Generate square thumbnail from specified crop area
  const getThumbnailImg = async (
    image: HTMLImageElement,
    crop: PixelCrop
  ): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Output dimensions - 200x200 square
    const outputSize = 200;
    canvas.width = outputSize;
    canvas.height = outputSize;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      outputSize,
      outputSize
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas to Blob failed'));
        },
        'image/jpeg',
        0.9
      );
    });
  };

  const goToThumbnailStep = () => {
    if (!completedPortraitCrop) return;
    setStep('thumbnail');
  };

  const goBackToPortrait = () => {
    setStep('portrait');
  };

  const approvePhoto = async () => {
    if (!selectedTherapist || !completedPortraitCrop || !completedThumbCrop || !imgRef.current || !user) return;

    setSaving(true);
    try {
      // Generate both crops
      const croppedBlob = await getCroppedImg(imgRef.current, completedPortraitCrop);
      const thumbnailBlob = await getThumbnailImg(imgRef.current, completedThumbCrop);

      // Upload 1:3 portrait to photo.jpg
      const photoRef = ref(storage, `therapists/${selectedTherapist.uid}/photo.jpg`);
      await uploadBytes(photoRef, croppedBlob);
      const photoUrl = await getDownloadURL(photoRef);

      // Upload square thumbnail to thumb.jpg
      const thumbRef = ref(storage, `therapists/${selectedTherapist.uid}/thumb.jpg`);
      await uploadBytes(thumbRef, thumbnailBlob);
      const thumbnailUrl = await getDownloadURL(thumbRef);

      // Update therapist document
      await updateDoc(doc(db, 'events', event.id, 'therapists', selectedTherapist.uid), {
        photoUrl,
        thumbnailUrl,
        photoStatus: 'approved',
        photoApprovedAt: serverTimestamp(),
        photoApprovedBy: user.uid,
      });

      closeEditor();
    } catch (err: any) {
      console.error('Error approving photo:', err);
      alert(err.message || 'Failed to approve photo');
    } finally {
      setSaving(false);
    }
  };

  const rejectPhoto = async () => {
    if (!selectedTherapist || !user) return;
    if (!window.confirm('Reject this photo? The therapist will need to upload a new one.')) return;

    setSaving(true);
    try {
      await updateDoc(doc(db, 'events', event.id, 'therapists', selectedTherapist.uid), {
        photoStatus: 'rejected',
      });
      closeEditor();
    } catch (err: any) {
      console.error('Error rejecting photo:', err);
      alert(err.message || 'Failed to reject photo');
    } finally {
      setSaving(false);
    }
  };

  const uploadOnBehalf = async () => {
    if (!selectedTherapist || !uploadFile || !completedPortraitCrop || !completedThumbCrop || !imgRef.current || !user) return;

    setSaving(true);
    try {
      // First upload original
      const originalRef = ref(storage, `therapists/${selectedTherapist.uid}/original.jpg`);
      await uploadBytes(originalRef, uploadFile);
      const originalPhotoUrl = await getDownloadURL(originalRef);

      // Generate both crops
      const croppedBlob = await getCroppedImg(imgRef.current, completedPortraitCrop);
      const thumbnailBlob = await getThumbnailImg(imgRef.current, completedThumbCrop);

      // Upload 1:3 portrait to photo.jpg
      const photoRef = ref(storage, `therapists/${selectedTherapist.uid}/photo.jpg`);
      await uploadBytes(photoRef, croppedBlob);
      const photoUrl = await getDownloadURL(photoRef);

      // Upload square thumbnail to thumb.jpg
      const thumbRef = ref(storage, `therapists/${selectedTherapist.uid}/thumb.jpg`);
      await uploadBytes(thumbRef, thumbnailBlob);
      const thumbnailUrl = await getDownloadURL(thumbRef);

      // Update therapist document
      await updateDoc(doc(db, 'events', event.id, 'therapists', selectedTherapist.uid), {
        originalPhotoUrl,
        photoUrl,
        thumbnailUrl,
        photoStatus: 'approved',
        photoUploadedAt: serverTimestamp(),
        photoApprovedAt: serverTimestamp(),
        photoApprovedBy: user.uid,
      });

      closeEditor();
    } catch (err: any) {
      console.error('Error uploading photo:', err);
      alert(err.message || 'Failed to upload photo');
    } finally {
      setSaving(false);
    }
  };

  const closeEditor = () => {
    setSelectedTherapist(null);
    setUploadFile(null);
    setUploadPreview(null);
    setPortraitCrop(undefined);
    setCompletedPortraitCrop(undefined);
    setThumbCrop(undefined);
    setCompletedThumbCrop(undefined);
    setStep('portrait');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const pendingCount = therapists.filter(t => t.photoStatus === 'pending').length;

  if (loading) {
    return <div className="loading">Loading therapists...</div>;
  }

  // Photo Editor Modal
  if (selectedTherapist) {
    const imageToShow = uploadPreview || selectedTherapist.originalPhotoUrl;

    return (
      <div className="photo-editor-modal" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.9)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>
            {selectedTherapist.displayName} - {step === 'portrait' ? 'Step 1: Portrait Crop' : 'Step 2: Profile Picture'}
          </h2>
          <button onClick={closeEditor} className="btn btn-secondary">
            Close
          </button>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', justifyContent: 'center' }}>
          <div style={{
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            backgroundColor: step === 'portrait' ? '#3b82f6' : '#333',
            color: step === 'portrait' ? '#fff' : '#888',
          }}>
            1. Portrait (1:3)
          </div>
          <div style={{
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            backgroundColor: step === 'thumbnail' ? '#3b82f6' : '#333',
            color: step === 'thumbnail' ? '#fff' : '#888',
          }}>
            2. Profile Picture (Square)
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', flex: 1, overflow: 'hidden' }}>
          {/* Left: Image and Crop */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {imageToShow ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {step === 'portrait' ? (
                  <ReactCrop
                    crop={portraitCrop}
                    onChange={(c) => setPortraitCrop(c)}
                    onComplete={(c) => setCompletedPortraitCrop(c)}
                    aspect={PORTRAIT_ASPECT}
                    style={{ maxHeight: '70vh' }}
                  >
                    <img
                      ref={imgRef}
                      src={imageToShow}
                      alt="Crop preview"
                      onLoad={onImageLoadPortrait}
                      style={{ maxHeight: '70vh', maxWidth: '100%' }}
                      crossOrigin="anonymous"
                    />
                  </ReactCrop>
                ) : (
                  <ReactCrop
                    crop={thumbCrop}
                    onChange={(c) => setThumbCrop(c)}
                    onComplete={(c) => setCompletedThumbCrop(c)}
                    aspect={THUMB_ASPECT}
                    circularCrop
                    style={{ maxHeight: '70vh' }}
                  >
                    <img
                      ref={imgRef}
                      src={imageToShow}
                      alt="Crop preview"
                      onLoad={onImageLoadThumb}
                      style={{ maxHeight: '70vh', maxWidth: '100%' }}
                      crossOrigin="anonymous"
                    />
                  </ReactCrop>
                )}
              </div>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #444',
                borderRadius: '8px',
              }}>
                <p style={{ color: '#888' }}>No photo uploaded. Upload one below.</p>
              </div>
            )}

            {/* Upload on behalf */}
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
              <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Upload photo on behalf of therapist:</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ marginBottom: '0.5rem' }}
              />
            </div>
          </div>

          {/* Right: Actions and Info */}
          <div style={{ width: '300px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ padding: '1rem', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0 }}>Photo Status</h3>
              <p>
                Current: <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor:
                    selectedTherapist.photoStatus === 'approved' ? '#22c55e' :
                    selectedTherapist.photoStatus === 'pending' ? '#f59e0b' :
                    selectedTherapist.photoStatus === 'rejected' ? '#ef4444' :
                    '#666',
                }}>
                  {selectedTherapist.photoStatus || 'None'}
                </span>
              </p>
            </div>

            <div style={{ padding: '1rem', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0 }}>
                {step === 'portrait' ? 'Portrait Crop' : 'Profile Picture'}
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#aaa' }}>
                {step === 'portrait' ? (
                  <>
                    Aspect ratio: 1:3 (portrait tall)<br/>
                    Output: 400 x 1200 pixels<br/><br/>
                    <strong>This is shown to players when booking.</strong>
                  </>
                ) : (
                  <>
                    Aspect ratio: 1:1 (square)<br/>
                    Output: 200 x 200 pixels<br/><br/>
                    <strong>Position the circle over the face.</strong><br/>
                    This is used for round profile pictures in admin views.
                  </>
                )}
              </p>
            </div>

            {/* Preview thumbnail */}
            {step === 'thumbnail' && completedThumbCrop && imgRef.current && (
              <div style={{ padding: '1rem', backgroundColor: '#1a1a2e', borderRadius: '8px', textAlign: 'center' }}>
                <h3 style={{ marginTop: 0 }}>Preview</h3>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  margin: '0 auto',
                  border: '3px solid #3b82f6',
                }}>
                  <canvas
                    id="thumb-preview"
                    width={80}
                    height={80}
                    style={{ width: '80px', height: '80px' }}
                    ref={(canvas) => {
                      if (canvas && imgRef.current && completedThumbCrop) {
                        const ctx = canvas.getContext('2d');
                        if (ctx) {
                          const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
                          const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
                          ctx.drawImage(
                            imgRef.current,
                            completedThumbCrop.x * scaleX,
                            completedThumbCrop.y * scaleY,
                            completedThumbCrop.width * scaleX,
                            completedThumbCrop.height * scaleY,
                            0,
                            0,
                            80,
                            80
                          );
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: 'auto' }}>
              {step === 'portrait' ? (
                <>
                  <button
                    onClick={goToThumbnailStep}
                    disabled={!completedPortraitCrop || !imageToShow}
                    className="btn btn-primary btn-large"
                  >
                    Next: Set Profile Picture
                  </button>
                  {imageToShow && !uploadPreview && (
                    <button
                      onClick={rejectPhoto}
                      disabled={saving}
                      className="btn btn-danger"
                    >
                      Reject Photo
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={goBackToPortrait}
                    className="btn btn-secondary"
                  >
                    Back to Portrait
                  </button>
                  {uploadPreview ? (
                    <button
                      onClick={uploadOnBehalf}
                      disabled={saving || !completedPortraitCrop || !completedThumbCrop}
                      className="btn btn-success btn-large"
                    >
                      {saving ? 'Saving...' : 'Save & Approve'}
                    </button>
                  ) : (
                    <button
                      onClick={approvePhoto}
                      disabled={saving || !completedPortraitCrop || !completedThumbCrop}
                      className="btn btn-success btn-large"
                    >
                      {saving ? 'Saving...' : 'Approve Photo'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="therapist-photos">
      <div className="section-header">
        <h2>Therapist Photos</h2>
        {pendingCount > 0 && (
          <span style={{
            marginLeft: '1rem',
            padding: '4px 12px',
            backgroundColor: '#f59e0b',
            borderRadius: '12px',
            fontSize: '0.875rem',
            fontWeight: 600,
          }}>
            {pendingCount} pending approval
          </span>
        )}
      </div>

      {therapists.length === 0 ? (
        <div className="empty-state">
          <p>No therapists have joined this event yet.</p>
        </div>
      ) : (
        <div className="photos-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '1rem',
          marginTop: '1rem',
        }}>
          {therapists.map((therapist) => (
            <div
              key={therapist.uid}
              onClick={() => setSelectedTherapist(therapist)}
              style={{
                backgroundColor: '#1a1a2e',
                borderRadius: '8px',
                padding: '1rem',
                cursor: 'pointer',
                border: therapist.photoStatus === 'pending' ? '2px solid #f59e0b' : '2px solid transparent',
                transition: 'transform 0.2s, border-color 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{
                aspectRatio: '1/3',
                backgroundColor: '#0d0d1a',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '0.5rem',
              }}>
                {therapist.photoUrl && therapist.photoStatus === 'approved' ? (
                  <img
                    src={therapist.photoUrl}
                    alt={therapist.displayName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : therapist.originalPhotoUrl ? (
                  <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <img
                      src={therapist.originalPhotoUrl}
                      alt={therapist.displayName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                    }}>
                      {therapist.photoStatus || 'Pending'}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '3rem',
                    color: '#444',
                  }}>
                    {therapist.displayName.charAt(0)}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontWeight: 600 }}>{therapist.displayName}</p>
                <p style={{
                  margin: '0.25rem 0 0 0',
                  fontSize: '0.75rem',
                  color:
                    therapist.photoStatus === 'approved' ? '#22c55e' :
                    therapist.photoStatus === 'pending' ? '#f59e0b' :
                    therapist.photoStatus === 'rejected' ? '#ef4444' :
                    '#888',
                }}>
                  {therapist.photoStatus || 'No photo'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
